import { Manager, logger, appDataCacheUtils } from "../../../core";
import { determineAppAccess } from "../../../core/utils/appAccessUtils";
import { navigationManager } from "../../../managers/navigation-manager";
import {
  ensureWorkspaceServiceWorkerWithTimeout,
  notifyWorkspaceServiceWorker,
} from "../../../workspace/serviceWorkerBridge";
import { WorkspaceToolSystem } from "../../../systems/core/instances/workspace/base/WorkspaceToolSystem.js";
import { initializeBrowserFilesystem } from "../../../systems/core/instances/workspace/shared/browserFilesystem.js";

export class PlayspaceManager extends Manager {
  constructor() {
    super();
    this.workspaceToolSystem = new WorkspaceToolSystem();
    this.filesystem = null;
    this.browserFsInitializedFor = null;
    // Prevent concurrent load operations
    this.isLoadingApp = false;
    this.loadingAppId = null;
    this.loadPromise = null;
  }

  async initStates() {
    logger.log(
      { tags: "manager|playspace|init", color1: "cyan" },
      "Initializing Playspace manager"
    );
  }

  async openAppSettings(appId) {
    if (!appId) return;
    await navigationManager.navigateToPath(`/app-settings/${appId}`);
  }

  async loadApp(appId, user, options = {}) {
    try {
      if (!appId) {
        return { error: 'missing_app_id' };
      }

      // Prevent concurrent loads - if already loading this app, return the existing promise
      if (this.isLoadingApp && this.loadingAppId === appId && this.loadPromise) {
        logger.log(
          { tags: "manager|playspace|load", color1: "yellow" },
          "Load already in progress for this app, waiting for existing load",
          { appId }
        );
        return await this.loadPromise;
      }

      // If loading a different app, wait for it to complete first
      if (this.isLoadingApp && this.loadingAppId !== appId) {
        logger.log(
          { tags: "manager|playspace|load", color1: "yellow" },
          "Waiting for current load to complete before starting new load",
          { currentAppId: this.loadingAppId, requestedAppId: appId }
        );
        // Wait for current load to finish (may succeed or fail)
        if (this.loadPromise) {
          try {
            await this.loadPromise;
          } catch (error) {
            // Previous load failed - clear its state before starting new load
            logger.warn(
              { tags: "manager|playspace|load", color1: "yellow" },
              "Previous load failed, clearing state before starting new load",
              { previousAppId: this.loadingAppId, newAppId: appId, error: error.message }
            );
            // Clear the failed load's state
            if (this.loadingAppId && this.loadingAppId !== appId) {
              this.isLoadingApp = false;
              this.loadingAppId = null;
              this.loadPromise = null;
            }
          }
        }
      }

      // Set loading state
      this.isLoadingApp = true;
      this.loadingAppId = appId;

      // Create and store the load promise (options may include onProgress)
      this.loadPromise = this._executeLoadApp(appId, user, options);

      try {
        const result = await this.loadPromise;
        return result;
      } finally {
        // Clear loading state only if this was the active load
        if (this.loadingAppId === appId) {
          this.isLoadingApp = false;
          this.loadingAppId = null;
          this.loadPromise = null;
        }
      }
    } catch (error) {
      // Clear loading state on error - always clear if this was the active load
      // Note: appId parameter is the requested app, loadingAppId is the currently loading app
      const wasActiveLoad = this.loadingAppId === appId;
      if (wasActiveLoad) {
        this.isLoadingApp = false;
        this.loadingAppId = null;
        this.loadPromise = null;
      }
      logger.error(
        { tags: "manager|playspace|error", color1: "red", includeSource: true },
        "Failed to load playspace app",
        { appId, loadingAppId: this.loadingAppId, wasActiveLoad, error }
      );
      return { error: 'error' };
    }
  }

  async _executeLoadApp(appId, user, options = {}) {
    const onProgress = options.onProgress;
    const report = (percent, message) => {
      if (typeof onProgress === 'function') onProgress({ percent, message });
    };

    try {
      logger.log(
        { tags: "manager|playspace|load", color1: "cyan" },
        "Loading app for playspace",
        { appId, user: user?._id || user?.id, isAnonymous: !user }
      );

      report(0, "Fetching app data...");
      const appResult = await appDataCacheUtils.fetchAppData(appId);
      const appData = appResult?.data || null;
      if (!appResult?.ok || !appData?.app) {
        return { error: appData?.error || 'not_found' };
      }

      const app = appData.app;
      app.id = app._id?.toString?.() || app.id;
      report(10, "App data loaded");

      // For anonymous users, only allow access to public apps
      if (!user) {
        if (!app.isPublic) {
          return { error: 'forbidden' };
        }

        report(15, "Preparing service worker...");
        // Handle app type setup for anonymous users (especially important for browser apps)
        const typeInfo = this.handleAppType(app, null);

        // For browser apps, anonymous users still need filesystem setup
        const appType = (app?.appType || '').toLowerCase();
        if (appType === 'browser') {
          try {
            const swWait = await ensureWorkspaceServiceWorkerWithTimeout();
            if (swWait.timedOut) {
              logger.warn(
                { tags: "manager|playspace|service-worker", color1: "yellow" },
                "Service worker registration did not settle within timeout; continuing sync"
              );
            }
            report(18, "Syncing files from cloud...");
            const syncResult = await this.loadBrowserAppFiles(app, {
              onProgress: (syncPercent, msg) => {
                const p = 18 + (syncPercent / 100) * 72;
                report(p, msg);
              },
            });
            if (syncResult?.entryFileExists !== undefined) {
              app.entryFileExists = syncResult.entryFileExists;
            }
            report(92, "Preparing preview...");
          } catch (error) {
            logger.error(
              { tags: "manager|playspace|filesystem", color1: "red", includeSource: true },
              "Failed to sync playspace files for anonymous user",
              error
            );
          }
        }

        app.previewUrl =
          typeInfo?.iframeSrc ||
          app.previewUrl ||
          app.playUrl ||
          'about:blank';
        app.typeInfo = typeInfo;
        report(100, "Ready");

        // Anonymous user can only view public apps
        return {
          error: null,
          app,
          membership: null,
          canView: true,
          isOwner: false,
          isAdmin: false,
          isMember: false,
          isPublicViewer: true,
        };
      }

      // Authenticated user - determine access using centralized utility
      const userId = user?._id || user?.id || user?.userId;
      let membership = null;
      if (userId) {
        const memRes = await fetch(
          `/api/app-memberships/by-app?appId=${encodeURIComponent(appId)}&userId=${encodeURIComponent(userId)}`,
          { credentials: 'include' }
        );
        const memData = memRes.ok ? await memRes.json() : null;
        membership = memData?.membership || null;
      }

      report(12, "Checking access...");
      const access = determineAppAccess(app, user, membership);
      if (!access.canView) {
        return { error: 'forbidden' };
      }

      // Resolve owner display name (avoid email)
      let ownerDisplay = app.ownerUsername || '';
      if (membership) {
        try {
          const allRes = await fetch(
            `/api/app-memberships/by-app/all?appId=${encodeURIComponent(appId)}`,
            { credentials: 'include' }
          );
          if (allRes.ok) {
            const allData = await allRes.json();
            const members = allData?.memberships || [];
            const ownerMember = members.find(
              (m) =>
                m.role === 'owner' ||
                (app.ownerId && m.userId && String(m.userId) === String(app.ownerId))
            );
            if (ownerMember) {
              ownerDisplay = ownerMember.username || ownerMember.name || ownerDisplay || 'Owner';
            }
          }
        } catch (err) {
          logger.warn(
            { tags: "manager|playspace|owner", color1: "yellow" },
            "Failed to resolve owner display name (continuing)",
            err
          );
        }
      }
      app.ownerDisplayName = ownerDisplay;
      report(15, "Preparing service worker...");

      // Type-specific handling
      const appType = (app?.appType || '').toLowerCase();
      if (appType === 'browser') {
        try {
          const swWait = await ensureWorkspaceServiceWorkerWithTimeout();
          if (swWait.timedOut) {
            logger.warn(
              { tags: "manager|playspace|service-worker", color1: "yellow" },
              "Service worker registration did not settle within timeout; continuing sync"
            );
          }
          report(18, "Syncing files from cloud...");
          const syncResult = await this.loadBrowserAppFiles(app, {
            onProgress: (syncPercent, msg) => {
              const p = 18 + (syncPercent / 100) * 72;
              report(p, msg);
            },
          });
          if (syncResult?.entryFileExists !== undefined) {
            app.entryFileExists = syncResult.entryFileExists;
          }
        } catch (error) {
          logger.error(
            { tags: "manager|playspace|filesystem", color1: "red", includeSource: true },
            "Failed to sync playspace files",
            error
          );
        }
        report(92, "Preparing preview...");
      }

      const typeInfo = this.handleAppType(app, membership);
      app.previewUrl =
        typeInfo?.iframeSrc ||
        app.previewUrl ||
        app.playUrl ||
        'about:blank';
      app.typeInfo = typeInfo;
      report(100, "Ready");

      return {
        error: null,
        app,
        membership: access.membership,
        canView: access.canView,
        isOwner: access.isOwner,
        isAdmin: access.isAdmin,
        isMember: access.isMember,
        isPublicViewer: access.isPublicViewer,
      };
    } catch (error) {
      logger.error(
        { tags: "manager|playspace|error", color1: "red", includeSource: true },
        "Failed to execute load playspace app",
        error
      );
      throw error; // Re-throw to be caught by outer try-catch
    }
  }

  /**
   * Central handler for app type branching
   */
  handleAppType(app, membership) {
    const appType = (app?.appType || '').toLowerCase();
    switch (appType) {
      case 'browser':
        return this.handleBrowserApp(app, membership);
      case 'website':
        return this.handleWebsiteApp(app, membership);
      case 'desktop':
        return this.handleDesktopApp(app, membership);
      default:
        return { mode: 'unknown', iframeSrc: app?.previewUrl || app?.playUrl || 'about:blank' };
    }
  }

  /**
   * Browser app: no droplet, use browser-hosted preview
   */
  handleBrowserApp(app) {
    // Use entry file path from app settings (required, no fallback)
    // Entry file path is relative to project root (e.g., "application/index.html")
    const entryFilePath = app?.settings?.entryFilePath;
    if (!entryFilePath) {
      // Return error state if entry file path not configured
      return {
        mode: 'browser',
        iframeSrc: 'about:blank',
        requiresDroplet: false,
        error: 'Entry file path not configured for this app'
      };
    }

    // Check if entry file exists - if not, return blank iframe
    if (app.entryFileExists === false) {
      return {
        mode: 'browser',
        iframeSrc: 'about:blank',
        requiresDroplet: false,
        error: 'Entry file not found'
      };
    }

    // Remove leading slash only (entry file path is relative to project root)
    const entryPath = entryFilePath.startsWith('/') ? entryFilePath.substring(1) : entryFilePath;
    const appId = app?.id;

    return {
      mode: 'browser',
      iframeSrc: appId
        ? `/workspace-files/${encodeURIComponent(appId)}/${entryPath}`
        : 'about:blank',
      requiresDroplet: false,
    };
  }

  /**
   * Website app: workspace would need droplet; playspace typically does not
   */
  handleWebsiteApp(app) {
    return {
      mode: 'website',
      iframeSrc: app?.previewUrl || app?.playUrl || 'about:blank',
      requiresDroplet: false,
    };
  }

  /**
   * Desktop app: playspace/workspace require droplet (parsec-like)
   */
  handleDesktopApp(app) {
    return {
      mode: 'desktop',
      iframeSrc: app?.previewUrl || app?.playUrl || 'about:blank',
      requiresDroplet: true,
    };
  }

  async initializeBrowserAppFilesystem(app) {
    if (!app?.id) {
      return;
    }
    if (this.browserFsInitializedFor === app.id && this.filesystem) {
      return;
    }

    const { filesystem, fsName } = await initializeBrowserFilesystem({
      appId: app.id,
      toolSystem: this.workspaceToolSystem,
      includeGit: true,
      logTags: "playspace|health|filesystem",
      fsPrefix: "revolt-app-playspace",
    });

    this.filesystem = filesystem;
    this.browserFsInitializedFor = app.id;

    logger.log(
      { tags: "manager|playspace|filesystem", color1: "lime" },
      "Browser app filesystem initialized for playspace",
      { appId: app.id, fsName }
    );
  }

  async loadBrowserAppFiles(app, options = {}) {
    const onProgress = options.onProgress;

    if (!app?.id) {
      return { entryFileExists: undefined };
    }

    // Ensure we're not loading concurrently for a different app
    if (this.isLoadingApp && this.loadingAppId !== app.id) {
      logger.warn(
        { tags: "manager|playspace|filesystem", color1: "yellow" },
        "Cannot load browser app files - different app is currently loading",
        { requestedAppId: app.id, loadingAppId: this.loadingAppId }
      );
      return { entryFileExists: undefined };
    }

    const report = (p, msg) => {
      if (typeof onProgress === 'function') onProgress(p, msg);
    };

    // Step 1: Initialize Lightning FS (must complete before sync)
    report(2, "Initializing filesystem...");
    await this.initializeBrowserAppFilesystem(app);

    // Verify filesystem is ready before proceeding
    if (!this.filesystem?.promises) {
      logger.error(
        { tags: "manager|playspace|filesystem", color1: "red" },
        "Filesystem not initialized after initializeBrowserAppFilesystem",
        { appId: app.id }
      );
      return { entryFileExists: undefined };
    }

    // Step 2: Sync files from cloud to temporary directory (must complete before copy)
    report(5, "Downloading files...");
    const result =
      await this.workspaceToolSystem.syncFromCloudTool.syncFromCloudToTemp(
        app.id,
        true, // clearExisting
        { onProgress: (p, msg) => report(5 + (p / 100) * 75, msg) }
      );

    if (!result.success) {
      const error = new Error(result.message || result.error || "Sync failed");
      error.type = result.data?.errorType || "sync_error";
      error.status = result.data?.status;
      error.endpoint = result.data?.endpoint;
      throw error;
    }

    // Step 3: Verify filesystem is still available after sync
    const fsPromises = this.filesystem?.promises;
    if (!fsPromises) {
      logger.error(
        { tags: "manager|playspace|filesystem", color1: "red" },
        "Filesystem promises not available after sync",
        { appId: app.id }
      );
      return { entryFileExists: undefined };
    }

    // Step 4: Apply cloud temp files to local filesystem (must complete before checking entry file)
    // Playspace always applies cloud version - no safety checks needed
    // Clear existing files and apply cloud version directly
    report(82, "Applying files to playspace...");
    await this.applyCloudTempToLocal(fsPromises);

    // Step 5: Check if entry file exists (must complete before notifying service worker)
    report(90, "Verifying files...");
    const entryFileExists = await this.checkEntryFileExists(fsPromises, app);

    // Step 6: Notify service worker (final step)
    report(97, "Finalizing...");
    await this.notifyServiceWorkerForApp(app);

    return {
      entryFileExists,
      fileCount: result.data?.fileCount || 0,
      requiresDecision: false // Playspace never requires user decision
    };
  }


  async syncCloudToTemp(appId) {
    return this.workspaceToolSystem.syncFromCloudTool.syncFromCloudToTemp(
      appId,
      true
    );
  }

  async applyCloudTempToLocal(fsPromises) {
    await this.clearDirectory(fsPromises, "/files");
    await this.copyDirectoryRecursive(fsPromises, "/__cloud/files", "/files");
    await this.clearDirectory(fsPromises, "/__cloud");
  }

  async clearDirectory(fsPromises, dirPath) {
    try {
      await fsPromises.stat(dirPath);
    } catch (error) {
      if (error.code === "ENOENT") {
        return;
      }
      throw error;
    }
    await this.removeDirectoryRecursive(fsPromises, dirPath);
  }

  async copyDirectoryRecursive(fsPromises, srcDir, destDir) {
    await fsPromises.mkdir(destDir, { recursive: true });
    const entries = await fsPromises.readdir(srcDir);
    for (const entry of entries) {
      const srcPath = `${srcDir}/${entry}`;
      const destPath = `${destDir}/${entry}`;
      
      // Handle potential race conditions - file might be deleted between readdir and stat
      let stats;
      try {
        stats = await fsPromises.stat(srcPath);
      } catch (error) {
        // If file doesn't exist (ENOENT), skip it - it may have been removed during concurrent operations
        if (error.code === "ENOENT") {
          logger.warn(
            { tags: "manager|playspace|filesystem", color1: "yellow" },
            "File not found during copy, skipping",
            { srcPath, error: error.message }
          );
          continue;
        }
        // Re-throw other errors
        throw error;
      }
      
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      if (isDir) {
        // Handle potential race conditions - directory might be deleted between stat and recursive call
        try {
          await this.copyDirectoryRecursive(fsPromises, srcPath, destPath);
        } catch (error) {
          // If directory was deleted during recursion, skip it
          if (error.code === "ENOENT") {
            logger.warn(
              { tags: "manager|playspace|filesystem", color1: "yellow" },
              "Directory deleted during recursive copy, skipping",
              { srcPath, error: error.message }
            );
            continue;
          }
          // Re-throw other errors
          throw error;
        }
      } else {
        // Read and write with error handling for race conditions
        try {
          const buffer = await fsPromises.readFile(srcPath);
          await fsPromises.writeFile(destPath, buffer);
        } catch (error) {
          // If file was deleted during read, skip it
          if (error.code === "ENOENT") {
            logger.warn(
              { tags: "manager|playspace|filesystem", color1: "yellow" },
              "File deleted during copy operation, skipping",
              { srcPath, error: error.message }
            );
            continue;
          }
          // Re-throw other errors
          throw error;
        }
      }
    }
  }

  async removeDirectoryRecursive(fsPromises, dirPath) {
    try {
      const entries = await fsPromises.readdir(dirPath);
      for (const entry of entries) {
        const fullPath = `${dirPath}/${entry}`;
        const stats = await fsPromises.stat(fullPath);
        const isDir =
          typeof stats.isDirectory === "function"
            ? stats.isDirectory()
            : stats.type === "dir";
        if (isDir) {
          await this.removeDirectoryRecursive(fsPromises, fullPath);
        } else {
          await fsPromises.unlink(fullPath);
        }
      }
      await fsPromises.rmdir(dirPath);
    } catch (error) {
      logger.warn(
        { tags: "manager|playspace|filesystem", color1: "yellow" },
        "Failed to remove directory recursively",
        { dirPath, error: error.message }
      );
    }
  }

  async checkGitExists(fsPromises, dirPath) {
    try {
      await fsPromises.stat(`${dirPath}/.git`);
      return true;
    } catch (error) {
      if (error.code === "ENOENT") {
        return false;
      }
      return false;
    }
  }

  async hasUserFiles(fsPromises, dirPath) {
    try {
      const entries = await fsPromises.readdir(dirPath);
      for (const entry of entries) {
        if (entry === ".git" || entry === ".revolt") {
          continue;
        }
        const fullPath = `${dirPath}/${entry}`;
        const stats = await fsPromises.stat(fullPath);
        const isDir =
          typeof stats.isDirectory === "function"
            ? stats.isDirectory()
            : stats.type === "dir";
        if (!isDir) {
          return true;
        }
        const hasFiles = await this.hasUserFiles(fsPromises, fullPath);
        if (hasFiles) {
          return true;
        }
      }
      return false;
    } catch (error) {
      if (error.code === "ENOENT") {
        return false;
      }
      return false;
    }
  }

  async getHeadInfo(dirPath) {
    if (!this.workspaceToolSystem?.git || !this.filesystem?.promises) {
      return null;
    }
    try {
      const headOid = await this.workspaceToolSystem.git.resolveRef({
        fs: this.filesystem,
        dir: dirPath,
        ref: "HEAD",
      });
      const commits = await this.workspaceToolSystem.git.log({
        fs: this.filesystem,
        dir: dirPath,
        ref: headOid,
        depth: 1,
      });
      const timestampSeconds = commits?.[0]?.commit?.author?.timestamp || null;
      return {
        head: headOid,
        timestamp: timestampSeconds ? timestampSeconds * 1000 : null,
      };
    } catch (error) {
      return null;
    }
  }

  async compareHeadRelation(localHead, cloudHead, localDir, cloudDir, localDirty) {
    if (localHead && cloudHead && localHead === cloudHead) {
      return localDirty ? "local_dirty" : "same";
    }
    if (!localHead && !cloudHead) {
      return localDirty ? "local_dirty" : "unknown";
    }
    if (!cloudHead) {
      return localDirty ? "local_dirty" : "cloud_empty";
    }
    if (!localHead) {
      return "local_empty";
    }
    const cloudHasLocal = await this.isAncestor(
      cloudDir,
      localHead,
      cloudHead
    );
    if (cloudHasLocal) {
      return "cloud_newer";
    }
    const localHasCloud = await this.isAncestor(
      localDir,
      cloudHead,
      localHead
    );
    if (localHasCloud) {
      return "local_newer";
    }
    return "diverged";
  }

  async isAncestor(dirPath, ancestorOid, descendantOid) {
    if (!this.workspaceToolSystem?.git || !this.filesystem?.promises) {
      return false;
    }
    try {
      const commits = await this.workspaceToolSystem.git.log({
        fs: this.filesystem,
        dir: dirPath,
        ref: descendantOid,
        depth: 2000,
      });
      return commits.some((commit) => commit.oid === ancestorOid);
    } catch (error) {
      return false;
    }
  }

  async hasWorkingTreeChanges(dirPath) {
    if (!this.workspaceToolSystem?.git || !this.filesystem) {
      return false;
    }
    try {
      const statusMatrix = await this.workspaceToolSystem.git.statusMatrix({
        fs: this.filesystem,
        dir: dirPath,
        filter: (filePath) => {
          return (
            !filePath.startsWith(".git/") &&
            filePath !== ".git" &&
            !filePath.startsWith(".revolt/") &&
            filePath !== ".revolt"
          );
        },
      });

      return statusMatrix.some(
        ([, headStatus, workdirStatus, stageStatus]) =>
          headStatus !== stageStatus ||
          (workdirStatus !== 1 && workdirStatus !== stageStatus)
      );
    } catch (error) {
      return false;
    }
  }

  async checkEntryFileExists(fsPromises, app) {
    const entryFilePath = app?.settings?.entryFilePath || "";
    if (!entryFilePath) {
      return undefined;
    }
    const normalizedEntry = entryFilePath.replace(/^\/+/, "");
    try {
      await fsPromises.stat(`/files/${normalizedEntry}`);
      return true;
    } catch (error) {
      if (error.code === "ENOENT") {
        return false;
      }
      return undefined;
    }
  }

  async notifyServiceWorkerForApp(app) {
    const entryFilePath = app?.settings?.entryFilePath || null;
    await notifyWorkspaceServiceWorker({
      type: "WORKSPACE_APP_UPDATED",
      appId: app.id,
      entryFilePath: entryFilePath || null,
      isPlayspace: true,
    });
  }
}

